import React, { useState, useCallback } from 'react';
import './ESTCalculator.css';

const PENCIL = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const LOCK = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

function fmt(n: number): string {
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function pct(n: number): string {
  return (n >= 0 ? '+' : '') + n.toFixed(2) + '%';
}

interface Params {
  entryBuf: number;
  slBuf: number;
  tgtBuf: number;
}

interface ParamLocked {
  entryBuf: boolean;
  slBuf: boolean;
  tgtBuf: boolean;
}

type ParamKey = keyof Params;

const ESTCalculator: React.FC = () => {
  const [high, setHigh] = useState('');
  const [low, setLow] = useState('');
  const [lastChanged, setLastChanged] = useState<'high' | 'low' | null>(null);
  const [params, setParams] = useState<Params>({ entryBuf: 0.25, slBuf: 0.20, tgtBuf: 12 });
  const [locked, setLocked] = useState<ParamLocked>({ entryBuf: true, slBuf: true, tgtBuf: true });

  const H = parseFloat(high);
  const L = parseFloat(low);
  const valid = !isNaN(H) && !isNaN(L) && H > 0 && L > 0 && H >= L;

  let result: { entry: number; stop: number; target: number; riskPct: number; rwdPct: number; rr: number } | null = null;

  if (valid) {
    const entry = H * (1 + params.entryBuf / 100);
    const stop = L * (1 - params.slBuf / 100);
    const target = entry * (1 + params.tgtBuf / 100);
    const riskPct = (entry - stop) / entry * 100;
    const rwdPct = (target - entry) / entry * 100;
    const rr = riskPct > 0 ? rwdPct / riskPct : 0;
    result = { entry, stop, target, riskPct, rwdPct, rr };
  }

  const toggleParam = useCallback((key: ParamKey) => {
    setLocked(prev => {
      const wasLocked = prev[key];
      return { ...prev, [key]: !wasLocked };
    });
  }, []);

  const updateParam = useCallback((key: ParamKey, val: string) => {
    const n = parseFloat(val);
    if (!isNaN(n) && n >= 0) setParams(prev => ({ ...prev, [key]: n }));
    else if (val === '' || val === '.') setParams(prev => ({ ...prev, [key]: 0 }));
  }, []);

  const paramDefs: { key: ParamKey; label: string; color: 'g' | 'r' | 'a' }[] = [
    { key: 'entryBuf', label: 'Entry Buffer', color: 'g' },
    { key: 'slBuf', label: 'SL Buffer', color: 'r' },
    { key: 'tgtBuf', label: 'Target', color: 'a' },
  ];

  const valuesConflict = high !== '' && !isNaN(H) && L !== '' && !isNaN(L) && H < L;
  const highErr = valuesConflict && lastChanged === 'high';
  const lowErr = valuesConflict && lastChanged === 'low';

  return (
    <div className="main-content est-page">
      <div className="est-hdr">
        <div className="est-hdr-tag">Tools / EST Calculator</div>
        <div className="est-hdr-row">
          <h1 className="est-hdr-title">Single Candle EST Calculator</h1>
          <div className={`est-status${result ? ' ready' : ''}`}>
            <span className="est-sdot" />
            {result ? 'Ready' : 'Enter values'}
          </div>
        </div>
        <div className="est-hdr-sub">Entry · Stoploss · Target — from a single candle's high &amp; low</div>
      </div>

      <div className="est-card">
        {/* Candle inputs */}
        <div className="est-section est-inputs">
          <div>
            <div className="est-field-label hi">
              <span className="est-dot g" />
              Candle High
            </div>
            <div className={`est-input-wrap hi${highErr ? ' err' : ''}`}>
              <span className="est-pfx">₹</span>
              <input
                className="est-input"
                type="number"
                inputMode="decimal"
                placeholder="0.00"
                value={high}
                onChange={e => { setHigh(e.target.value); setLastChanged('high'); }}
                min="0"
              />
            </div>
            {highErr && <div className="est-err">High must be ≥ Low</div>}

          </div>

          <div>
            <div className="est-field-label lo">
              <span className="est-dot r" />
              Candle Low
            </div>
            <div className={`est-input-wrap lo${lowErr ? ' err' : ''}`}>
              <span className="est-pfx">₹</span>
              <input
                className="est-input"
                type="number"
                inputMode="decimal"
                placeholder="0.00"
                value={low}
                onChange={e => { setLow(e.target.value); setLastChanged('low'); }}
                min="0"
              />
            </div>
            {lowErr && <div className="est-err">Low must be ≤ High</div>}
          </div>
        </div>

        {/* Parameters */}
        <div className="est-section est-params">
          <div className="est-params-head">Parameters</div>
          <div className="est-params-grid">
            {paramDefs.map(({ key, label, color }) => (
              <div key={key}>
                <div className={`est-param-label ${color}`}>
                  <span className={`est-dot ${color}`} />
                  {label}
                </div>
                <div className={`est-chip${!locked[key] ? ' unlocked' : ''}`}>
                  <input
                    className="est-chip-val"
                    type="number"
                    inputMode="decimal"
                    disabled={locked[key]}
                    value={params[key]}
                    onChange={e => updateParam(key, e.target.value)}
                    min="0"
                    step="0.1"
                  />
                  <span className="est-chip-unit">%</span>
                  <button
                    className={`est-chip-btn${!locked[key] ? ' unlocked' : ''}`}
                    onClick={() => toggleParam(key)}
                    title={locked[key] ? 'Edit parameter' : 'Lock parameter'}
                    type="button"
                  >
                    {locked[key] ? PENCIL : LOCK}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Results */}
        {result ? (
          <>
            <div className="est-results">
              <div className="est-rcell g">
                <div className="est-rlbl">Entry</div>
                <div className="est-rprice-row">
                  <span className="est-rpfx">₹</span>
                  <span className="est-rprice">{fmt(result.entry)}</span>
                </div>
                <div className="est-rpill">↑ {params.entryBuf}% above high</div>
              </div>

              <div className="est-rcell r">
                <div className="est-rlbl">Stoploss</div>
                <div className="est-rprice-row">
                  <span className="est-rpfx">₹</span>
                  <span className="est-rprice">{fmt(result.stop)}</span>
                </div>
                <div className="est-rpill">↓ {result.riskPct.toFixed(2)}% risk</div>
              </div>

              <div className="est-rcell a">
                <div className="est-rlbl">Target</div>
                <div className="est-rprice-row">
                  <span className="est-rpfx">₹</span>
                  <span className="est-rprice">{fmt(result.target)}</span>
                </div>
                <div className="est-rpill">↑ {result.rwdPct.toFixed(2)}% reward</div>
              </div>
            </div>

            <div className="est-footer">
              <div className="est-rr">
                R:R &nbsp;<strong>{result.rr.toFixed(2)}</strong>
              </div>
            </div>
          </>
        ) : (
          <div className="est-placeholder">
            <span className="est-placeholder-icon">📊</span>
            Enter candle high &amp; low to calculate levels
          </div>
        )}
      </div>
    </div>
  );
};

export default ESTCalculator;
